import { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useStyle } from "./Repayment.styles";
import { Tabs, Modal, Group } from "@mantine/core";
import Sales from "./Components/Tabs/Sales";
import useGetNoteSales from "../../hooks/useGetNoteSales";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Button, color } from "@funded-here-interface/common";
import Note from "./Components/Tabs/Note";
import Sku from "./Components/Tabs/Sku";
import Default from "./Components/Tabs/Default";
import { FaArrowLeftLong } from "react-icons/fa6";
import useCloseSNMutation from "../../hooks/useCloseSN";

import useDownloadNoteSales from "../../hooks/useDownloadNoteSales";
import useDownloadNoteSkuRepayment from "../../hooks/useDownloadNoteSkuRepayment";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";

enum RepaymentTab {
  SALES = "sales",
  SN = "sn",
  SKU = "sku",
  DEFAULT = "default",
}

const Repayment: FC = () => {
  const { classes } = useStyle();
  const { id } = useParams();
  const token = useSelector((state: RootState) => state.auth.token);
  const [modalOpen, setModalOpen] = useState(false);

  const useCloseSnMutation = useCloseSNMutation();
  const [canCloseNote, setCanCloseNote] = useState(false);
  const [initalRun, setInitalRun] = useState(true);
  const [noteId, setNoteId] = useState("");
  const [selectedTab, setSelectedTab] = useState(RepaymentTab.SALES);

  const downloadNoteSalesMutation = useDownloadNoteSales();
  const handleDownloadNoteSales = () => {
    if (id) {
      downloadNoteSalesMutation.mutate(
        {
          token,
          structuredNoteId: id,
        },
        {
          onSuccess: (data) => {
            downloadFile(data, "Note-Sales", FileType.CSV);
          },
          onError: (e: any) => {
            toast.error(e.message);
          },
        }
      );
    }
  };

  const downloadNoteRepaymentMutation = useDownloadNoteSkuRepayment();
  const handleDownloadNoteRepayment = () => {
    if (id) {
      downloadNoteRepaymentMutation.mutate(
        {
          token,
          structuredNoteId: id,
        },
        {
          onSuccess: (data) => {
            downloadFile(data, "Note-Repayment", FileType.CSV);
          },
          onError: (e: any) => {
            toast.error(e.message);
          },
        }
      );
    }
  };

  const updateCanCloseNote = (canClose: boolean, noteDisplayId: string) => {
    if (initalRun) {
      setCanCloseNote(canClose);
      setInitalRun(false);
    }
    setNoteId(noteDisplayId);
  };

  const handleCloseNote = () => {
    useCloseSnMutation.mutate(
      { structuredNoteId: parseInt(id!), token },
      {
        onSuccess: () => {
          setCanCloseNote(false);
          setModalOpen(false);
          toast.success("Note Closed Successfully");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div>
      <div>
        <div className={classes.header}>
          <Link to="/note-list">
            <FaArrowLeftLong style={{ color: color.FHADMIN, fontSize: 32 }} />
          </Link>
          <h1 className={classes.headerText}>Track Repayment</h1>
        </div>
        <h1 className={classes.text}>{`Note Id: ${noteId}`}</h1>
      </div>
      <Tabs defaultValue={RepaymentTab.SALES}>
        <Tabs.List>
          <Tabs.Tab
            value={RepaymentTab.SALES}
            onClick={() => {
              setSelectedTab(RepaymentTab.SALES);
            }}
          >
            Sales Proceeds
          </Tabs.Tab>
          <Tabs.Tab
            value={RepaymentTab.SN}
            onClick={() => {
              setSelectedTab(RepaymentTab.SN);
            }}
          >
            Repayment-Note
          </Tabs.Tab>
          <Tabs.Tab
            value={RepaymentTab.SKU}
            onClick={() => {
              setSelectedTab(RepaymentTab.SKU);
            }}
          >
            Repayment-SKU
          </Tabs.Tab>
          <Tabs.Tab
            value={RepaymentTab.DEFAULT}
            onClick={() => {
              setSelectedTab(RepaymentTab.DEFAULT);
            }}
          >
            Default
          </Tabs.Tab>
        </Tabs.List>

        <Modal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          title={<strong>Confirm Note Closure</strong>}
          centered
        >
          <p>
            You are about to close this note. Are you sure you want to proceed?
          </p>
          <Group position="center" mt="md">
            <Button
              onClick={() => setModalOpen(false)}
              backgroundColor="#ccc"
              textColor="#000"
              width="120px"
              children="Cancel"
            />
            <Button
              onClick={handleCloseNote}
              backgroundColor={color.FHGREEN}
              textColor="#fff"
              width="120px"
              children="Confirm"
            />
          </Group>
        </Modal>

        <div className={classes.button}>
          <Button
            onClick={() => setModalOpen(true)}
            backgroundColor={color.FHGREEN}
            textColor="#fff"
            children="Close Note"
            disabled={!canCloseNote}
            width="260px"
          />

          {(selectedTab === RepaymentTab.SALES ||
            selectedTab === RepaymentTab.SKU) && (
            <Button
              onClick={() => {
                selectedTab === RepaymentTab.SALES
                  ? handleDownloadNoteSales()
                  : handleDownloadNoteRepayment();
              }}
              backgroundColor={color.LOANTEXT}
              textColor="#fff"
              children="Download CSV"
              width="260px"
            />
          )}
        </div>

        <Tabs.Panel value={RepaymentTab.SALES} pt="xs">
          {/* <Sales
            isLoading={isLoading}
            data={data?.skus}
            error={error as Error}
          /> */}
          <Sales noteId={id!} onCanCloseNoteChange={updateCanCloseNote} />
        </Tabs.Panel>
        <Tabs.Panel value={RepaymentTab.SN} pt="xs">
          <Note noteId={id!} />
        </Tabs.Panel>
        <Tabs.Panel value={RepaymentTab.SKU} pt="xs">
          <Sku noteId={id!} />
        </Tabs.Panel>
        <Tabs.Panel value={RepaymentTab.DEFAULT} pt="xs">
          <Default noteId={id!} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

export default Repayment;
